// react
import { memo, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CloseIcon from '@mui/icons-material/Close';

// material ui
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import { useDataTypesGetCollectionQuery } from '@/api/generated/recalc/endpoints/v1/datatypes';

// shared components
import SensorTag from '@/components/ui/SensorTag';
import UnitTag from '@/components/ui/UnitTag';

// relative
import type {
  AggregateType,
  ChartSeries,
  ExpandedRecordedDataType,
  ReadUnitDto,
  SeriesVisibility,
} from '../types';
import { getStableSeriesColor } from '../utils/seriesColors';

// Highcharts default color palette (matches the chart series colors)
const AGGREGATE_LABELS: Record<AggregateType, string> = {
  Raw: 'Raw',
  Minutes5: '5min',
  Hour: '1h',
  Day: '1d',
};
function getSeriesColor(
  seriesId: string,
  chartSeries: ChartSeries[],
): string | undefined {
  return chartSeries.some((s) => s.id === seriesId)
    ? getStableSeriesColor(seriesId)
    : undefined;
}

function getInputCustomName(
  unit: ReadUnitDto,
  unitTypeInputId: number,
): string | undefined {
  if (unitTypeInputId === 0) return undefined;
  const customName = unit.UnitSensors?.find(
    (s) => s.UnitTypeInput?.UnitTypeInputId === unitTypeInputId,
  )?.CustomName?.trim();
  return customName || undefined;
}

// ─── SeriesLegendItem ────────────────────────────────────────────────────────

interface SeriesLegendItemProps {
  name: string;
  color?: string;
  /** Series exists in chartSeries (has data) */
  hasData: boolean;
  /** User has not hidden this series */
  isVisible: boolean;
  aggregateLabel?: string;
  /** Another series in the legend is being hovered */
  dimmed?: boolean;
  onToggle: () => void;
  onHoverIn?: () => void;
  onHoverOut?: () => void;
}

const SeriesLegendItem = memo(function SeriesLegendItem({
  name,
  color,
  hasData,
  isVisible,
  aggregateLabel,
  dimmed,
  onToggle,
  onHoverIn,
  onHoverOut,
}: SeriesLegendItemProps) {
  return (
    <Box
      role={hasData ? 'button' : undefined}
      tabIndex={hasData ? 0 : undefined}
      onClick={hasData ? onToggle : undefined}
      onMouseEnter={hasData && isVisible ? onHoverIn : undefined}
      onMouseLeave={hasData && isVisible ? onHoverOut : undefined}
      onKeyDown={
        hasData
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggle();
              }
            }
          : undefined
      }
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        cursor: hasData ? 'pointer' : 'default',
        opacity: !hasData ? 0.4 : dimmed ? 0.3 : isVisible ? 1 : 0.5,
        transition: 'opacity 0.15s ease',
        userSelect: 'none',
        borderRadius: 0.75,
        px: 0.5,
        py: 0.25,
        width: '100%',
        '&:hover': hasData ? { bgcolor: 'action.hover' } : {},
        '&:focus-visible': hasData
          ? {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 1,
            }
          : {},
      }}
    >
      {hasData ? (
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            flexShrink: 0,
            bgcolor: color ?? 'text.secondary',
          }}
        />
      ) : (
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            flexShrink: 0,
            border: '1.5px dashed',
            borderColor: 'text.disabled',
          }}
        />
      )}

      <Typography
        variant="caption"
        sx={{
          textDecoration: hasData && !isVisible ? 'line-through' : 'none',
          fontStyle: !hasData ? 'italic' : 'normal',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          lineHeight: 1.4,
          fontSize: '0.7rem',
          minWidth: 0,
        }}
      >
        {name}
      </Typography>

      {aggregateLabel && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled',
            fontSize: '0.6rem',
            ml: 'auto',
            pl: 0.5,
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {aggregateLabel}
        </Typography>
      )}
    </Box>
  );
});

// ─── UnitLegendCard ──────────────────────────────────────────────────────────

interface UnitLegendCardProps {
  unit: ReadUnitDto;
  datatypes: ExpandedRecordedDataType[];
  chartSeries: ChartSeries[];
  chartSeriesIds: Set<string>;
  seriesVisibility: SeriesVisibility;
  setSeriesVisibility: Dispatch<SetStateAction<SeriesVisibility>>;
  onSeriesHoverIn: (id: string) => void;
  onSeriesHoverOut: () => void;
  baseByDataType: Map<string, string>;
  onRemoveUnit?: (unitId: number) => void;
}

const UnitLegendCard = memo(function UnitLegendCard({
  unit,
  datatypes,
  chartSeries,
  chartSeriesIds,
  seriesVisibility,
  setSeriesVisibility,
  onSeriesHoverIn,
  onSeriesHoverOut,
  baseByDataType,
  onRemoveUnit,
}: UnitLegendCardProps) {
  const { t } = useTranslation(['view-data', 'datatypes']);

  const groupedByDataType = useMemo(() => {
    const map = new Map<string, ExpandedRecordedDataType[]>();
    datatypes.forEach((d) => {
      const arr = map.get(d.DataType) ?? [];
      arr.push(d);
      map.set(d.DataType, arr);
    });
    return map;
  }, [datatypes]);

  const dataTypeKeys = useMemo(
    () => Array.from(groupedByDataType.keys()).sort(),
    [groupedByDataType],
  );

  const tagTexts = Array.from(new Set(unit.Tags?.map((tag) => tag.Text)));
  const sensorIds = Array.from(
    new Set(unit.UnitSensors?.map((s) => s.Sensor?.SensorId).filter(Boolean)),
  );

  const handleToggleDataTypeForUnit = (dataType: string) => {
    const idsForDt = (groupedByDataType.get(dataType) ?? [])
      .map((d) => `${d.UnitId}-${d.DataType}-${d.UnitTypeInputId}`)
      .filter((id) => chartSeriesIds.has(id));

    if (idsForDt.length === 0) return;

    setSeriesVisibility((prev) => {
      const anyOn = idsForDt.some((id) => prev[id]?.visible !== false);
      const next = { ...prev };
      idsForDt.forEach((id) => {
        next[id] = { visible: !anyOn };
      });
      return next;
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Unit name */}
      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        gap={0.75}
        sx={{
          mb: 0.75,
          '& .remove-unit': { visibility: 'hidden' },
          '&:hover .remove-unit, & .remove-unit:focus-visible': {
            visibility: 'visible',
          },
        }}
      >
        <Tooltip title={`[${unit.UnitId}] ${unit.Name}`} placement="top">
          <Typography
            variant="subtitle2"
            fontWeight={600}
            noWrap
            sx={{ fontSize: '0.75rem', minWidth: 0 }}
          >
            [{unit.UnitId}] {unit.Name}
          </Typography>
        </Tooltip>
        {tagTexts.map((tag) => (
          <UnitTag key={tag} label={tag} />
        ))}
        {sensorIds.map((sid) => (
          <SensorTag key={sid} label={sid ?? ''} />
        ))}
        {onRemoveUnit && (
          <IconButton
            className="remove-unit"
            size="small"
            aria-label={t('legend.remove_unit', {
              defaultValue: 'Remove unit',
            })}
            onClick={() => onRemoveUnit(unit.UnitId)}
            sx={{ p: 0.25 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      {/* Series list grouped by dataType — section header is the toggle */}
      <Stack
        gap={2}
        flexDirection="row"
        divider={<Divider orientation="vertical" flexItem />}
        flexWrap="wrap"
      >
        {dataTypeKeys.map((dataType) => {
          const items = groupedByDataType.get(dataType) ?? [];
          const idsForDt = items
            .map((d) => `${d.UnitId}-${d.DataType}-${d.UnitTypeInputId}`)
            .filter((id) => chartSeriesIds.has(id));
          const hasAnyData = idsForDt.length > 0;
          const anyVisible =
            hasAnyData &&
            idsForDt.some((id) => seriesVisibility[id]?.visible !== false);
          return (
            <Box key={dataType}>
              {/* Clickable section header = toggle all of this dataType for this unit */}
              <Box
                role={hasAnyData ? 'button' : undefined}
                tabIndex={hasAnyData ? 0 : undefined}
                onClick={
                  hasAnyData
                    ? () => handleToggleDataTypeForUnit(dataType)
                    : undefined
                }
                onKeyDown={
                  hasAnyData
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleToggleDataTypeForUnit(dataType);
                        }
                      }
                    : undefined
                }
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: hasAnyData ? 'pointer' : 'default',
                  borderRadius: 0.5,
                  px: 0.5,
                  py: 0.25,
                  mb: 0.25,
                  userSelect: 'none',
                  '&:hover': hasAnyData ? { bgcolor: 'action.hover' } : {},
                  '&:focus-visible': hasAnyData
                    ? {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: 1,
                      }
                    : {},
                }}
              >
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: '2px',
                    flexShrink: 0,
                    bgcolor: anyVisible ? 'primary.main' : 'action.disabled',
                    opacity: !hasAnyData ? 0.4 : 1,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.6rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  {t(`datatypes:${dataType}`, { defaultValue: dataType })}
                  {baseByDataType.get(dataType) && (
                    <Box
                      component="span"
                      sx={{
                        ml: 0.5,
                        color: 'text.disabled',
                        textTransform: 'none',
                        letterSpacing: 'normal',
                      }}
                    >
                      {baseByDataType.get(dataType)}
                    </Box>
                  )}
                </Typography>
              </Box>

              <Stack spacing={0}>
                {items.map((dt) => {
                  const seriesId = `${dt.UnitId}-${dt.DataType}-${dt.UnitTypeInputId}`;
                  const hasData = chartSeriesIds.has(seriesId);
                  const isVisible =
                    seriesVisibility[seriesId]?.visible !== false;
                  const color = getSeriesColor(seriesId, chartSeries);
                  const customName = getInputCustomName(
                    unit,
                    dt.UnitTypeInputId,
                  );
                  const inputName =
                    customName ??
                    (dt.UnitTypeInputId === 0
                      ? t('view-data:total')
                      : (dt.Name ?? t('view-data:total')));
                  const foundSeries = chartSeries.find(
                    (s) => s.id === seriesId,
                  );
                  const aggregateLabel = foundSeries
                    ? AGGREGATE_LABELS[foundSeries.aggregateType]
                    : undefined;

                  return (
                    <SeriesLegendItem
                      key={seriesId}
                      name={inputName}
                      color={color}
                      hasData={hasData}
                      isVisible={isVisible}
                      aggregateLabel={aggregateLabel}
                      onToggle={() => {
                        setSeriesVisibility((prev) => ({
                          ...prev,
                          [seriesId]: {
                            visible: prev[seriesId]?.visible === false,
                          },
                        }));
                      }}
                      onHoverIn={() => onSeriesHoverIn(seriesId)}
                      onHoverOut={onSeriesHoverOut}
                    />
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
});

// ─── DataViewerLegend ────────────────────────────────────────────────────────

interface DataViewerLegendProps {
  selectedUnits: ReadUnitDto[];
  onRemoveUnit?: (unitId: number) => void;
  expandedRecordedDataTypes: ExpandedRecordedDataType[];
  chartSeries: ChartSeries[];
  seriesVisibility: SeriesVisibility;
  setSeriesVisibility: Dispatch<SetStateAction<SeriesVisibility>>;
  /** Called when pointer enters a series row — use to highlight it in the chart */
  onSeriesHoverIn?: (seriesId: string) => void;
  /** Called when pointer leaves a series row — use to reset chart highlight */
  onSeriesHoverOut?: () => void;
}

export default function DataViewerLegend({
  selectedUnits,
  onRemoveUnit,
  expandedRecordedDataTypes,
  chartSeries,
  seriesVisibility,
  setSeriesVisibility,
  onSeriesHoverIn,
  onSeriesHoverOut,
}: DataViewerLegendProps) {
  const { t } = useTranslation(['view-data', 'datatypes']);

  const { data: dataTypes } = useDataTypesGetCollectionQuery();

  const [, setHoveredSeriesId] = useState<string | null>(null);

  const handleSeriesHoverIn = (id: string) => {
    setHoveredSeriesId(id);
    onSeriesHoverIn?.(id);
  };

  const handleSeriesHoverOut = () => {
    setHoveredSeriesId(null);
    onSeriesHoverOut?.();
  };

  const byUnit = useMemo(() => {
    const map = new Map<number, ExpandedRecordedDataType[]>();
    expandedRecordedDataTypes.forEach((dt) => {
      const arr = map.get(dt.UnitId) ?? [];
      arr.push(dt);
      map.set(dt.UnitId, arr);
    });
    return map;
  }, [expandedRecordedDataTypes]);

  const allDataTypes = useMemo(
    () => Array.from(new Set(chartSeries.map((s) => s.dataType))).sort(),
    [chartSeries],
  );

  const chartSeriesIds = useMemo(
    () => new Set(chartSeries.map((s) => s.id)),
    [chartSeries],
  );

  const baseByDataType = useMemo(() => {
    const map = new Map<string, string>();
    dataTypes?.forEach((dt) => {
      if (dt.Base) map.set(dt.Key, dt.Base);
    });
    return map;
  }, [dataTypes]);

  // All total series (UnitTypeInputId === 0) that have data
  const totalSeriesIds = useMemo(
    () =>
      expandedRecordedDataTypes
        .filter((d) => d.UnitTypeInputId === 0)
        .map((d) => `${d.UnitId}-${d.DataType}-0`)
        .filter((id) => chartSeriesIds.has(id)),
    [expandedRecordedDataTypes, chartSeriesIds],
  );

  const anyTotalVisible = totalSeriesIds.some(
    (id) => seriesVisibility[id]?.visible !== false,
  );

  const handleToggleTotals = () => {
    setSeriesVisibility((prev) => {
      const next = { ...prev };
      totalSeriesIds.forEach((id) => {
        next[id] = { visible: !anyTotalVisible };
      });
      return next;
    });
  };

  const handleToggleDataType = (dataType: string) => {
    const idsForDt = chartSeries
      .filter((s) => s.dataType === dataType)
      .map((s) => s.id);
    setSeriesVisibility((prev) => {
      const anyOn = idsForDt.some((id) => prev[id]?.visible !== false);
      const next = { ...prev };
      idsForDt.forEach((id) => {
        next[id] = { visible: !anyOn };
      });
      return next;
    });
  };

  const anySeriesVisible = chartSeries.some(
    (s) => seriesVisibility[s.id]?.visible !== false,
  );

  const handleToggleAll = () => {
    setSeriesVisibility((prev) => {
      const next = { ...prev };
      chartSeries.forEach((s) => {
        next[s.id] = { visible: !anySeriesVisible };
      });
      return next;
    });
  };

  if (selectedUnits.length === 0) return null;

  return (
    <Box>
      {/* Global controls: cross-unit datatype toggles + totals toggle */}

      {/* Per-unit cards */}
      <Stack
        sx={{
          display: 'flex',
          gap: 1.25,
          flexDirection: 'column',
          justifyContent: 'flex-start',
        }}
        divider={<Divider orientation="horizontal" flexItem />}
      >
        {selectedUnits.map((unit) => {
          const datatypesForUnit = byUnit.get(unit.UnitId);
          if (!datatypesForUnit || datatypesForUnit.length === 0) return null;
          return (
            <UnitLegendCard
              key={unit.UnitId}
              unit={unit}
              datatypes={datatypesForUnit}
              chartSeries={chartSeries}
              chartSeriesIds={chartSeriesIds}
              seriesVisibility={seriesVisibility}
              setSeriesVisibility={setSeriesVisibility}
              onSeriesHoverIn={handleSeriesHoverIn}
              onSeriesHoverOut={handleSeriesHoverOut}
              baseByDataType={baseByDataType}
              onRemoveUnit={onRemoveUnit}
            />
          );
        })}
      </Stack>
      {(allDataTypes.length > 0 || totalSeriesIds.length > 0) && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            mt: 1.5,
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          <Chip
            label={t('legend.toggle_all', { defaultValue: 'All series' })}
            variant={anySeriesVisible ? 'filled' : 'outlined'}
            color={anySeriesVisible ? 'primary' : 'default'}
            size="small"
            onClick={handleToggleAll}
          />

          {allDataTypes.map((dt) => {
            const idsForDt = chartSeries
              .filter((s) => s.dataType === dt)
              .map((s) => s.id);
            const anyVisible = idsForDt.some(
              (id) => seriesVisibility[id]?.visible !== false,
            );
            return (
              <Chip
                key={dt}
                label={t(`datatypes:${dt}`, { defaultValue: dt })}
                variant={anyVisible ? 'filled' : 'outlined'}
                color={anyVisible ? 'primary' : 'default'}
                size="small"
                onClick={() => handleToggleDataType(dt)}
              />
            );
          })}

          {totalSeriesIds.length > 0 && (
            <Chip
              label={t('view-data:total')}
              variant={anyTotalVisible ? 'filled' : 'outlined'}
              color={anyTotalVisible ? 'secondary' : 'default'}
              size="small"
              onClick={handleToggleTotals}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
